import React from 'react';
import { Link } from 'react-router-dom';
import {
  Heart, Waves, Leaf, Sun, Target, Sparkles, Flame, Users, Compass, Palette,
  ChevronRight,
} from 'lucide-react';
import { manifestThemes } from '../data/manifestData';

const iconMap = { Heart, Waves, Leaf, Sun, Target, Sparkles, Flame, Users, Compass, Palette };

const ManifestPage = () => {
  return (
    <div className="pb-6" data-testid="manifest-page">
      {/* Hero — signature concentric-rings + sage-gold halo */}
      <div className="relative px-5 pt-8 pb-6 overflow-hidden animate-fade-in">
        <div
          className="absolute -top-24 -right-20 w-[420px] h-[420px] rounded-full pointer-events-none opacity-70"
          style={{ background: 'radial-gradient(circle, rgba(122,155,142,0.35) 0%, rgba(201,168,102,0.22) 40%, transparent 70%)' }}
          aria-hidden
        />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full border border-rbr-gold-soft/25 pointer-events-none" aria-hidden />
        <div className="absolute -top-28 -right-28 w-[360px] h-[360px] rounded-full border border-rbr-gold-soft/30 pointer-events-none" aria-hidden />
        <div className="absolute -top-16 -right-16 w-[220px] h-[220px] rounded-full border border-rbr-gold-soft/40 pointer-events-none" aria-hidden />

        <div className="relative z-10">
          <p className="rbr-eyebrow mb-2">Speak it · claim it</p>
          <h1 className="rbr-heading text-4xl mb-1.5 leading-tight">Manifest</h1>
          <p className="rbr-serif-italic text-rbr-sage-deep text-base">
            Ten themes to root you back into what you are becoming.
          </p>
        </div>
      </div>

      {/* Grid of theme tiles */}
      <div className="px-5">
        <div className="grid grid-cols-2 gap-3 rbr-stagger">
          {manifestThemes.map((t) => {
            const Icon = iconMap[t.icon] || Sparkles;
            return (
              <Link
                key={t.id}
                to={`/manifest/${t.id}`}
                data-testid={`manifest-tile-${t.id}`}
                className="group relative overflow-hidden rounded-3xl p-5 shadow-rbr-soft hover:-translate-y-1 hover:shadow-rbr-lift transition-all duration-300 border border-rbr-gold-soft/40"
                style={{
                  background: `linear-gradient(155deg, ${t.gradient[0]} 0%, ${t.gradient[1]} 100%)`,
                }}
              >
                {/* Subtle inner ring for depth */}
                <div
                  className="absolute -top-8 -right-8 w-24 h-24 rounded-full border border-white/25 pointer-events-none"
                  aria-hidden
                />
                <div
                  className="absolute -top-4 -right-4 w-16 h-16 rounded-full border border-white/15 pointer-events-none"
                  aria-hidden
                />

                <div className="relative z-10 flex flex-col h-full min-h-[132px]">
                  <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3
                    className="text-white text-lg font-semibold leading-tight mb-1"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {t.title}
                  </h3>
                  <p className="text-white/85 text-[11px] leading-snug rbr-serif-italic">
                    {t.tagline}
                  </p>

                  <div className="mt-auto pt-3 flex items-center gap-1 text-white/80 text-[10px] uppercase tracking-[0.22em] font-medium">
                    <span>{t.affirmations.length} affirmations</span>
                    <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Footer whisper */}
      <p className="rbr-serif-italic text-center text-rbr-sage-deep text-sm mt-8 px-8 leading-relaxed">
        Whisper them. Write them. Let them do their quiet work.
      </p>
    </div>
  );
};

export default ManifestPage;
